import { useMemo, useState, useEffect } from "react";
import { useSearch } from "@tanstack/react-router";
import { SearchBar } from "@/components/venues/SearchBar";
import { VenueCard } from "@/components/venues/VenueCard";
import { LOCATIONS, PRICE_RANGES, FACILITIES, BROWSABLE_CATEGORIES, compareByGuestRating, type VenueCategory, type Venue, getStartingPrice } from "@/data/venues";
import { VenueService } from "@/services/venue.service";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Hotel, Palmtree, Building2, PartyPopper, Trees, Waves, Building, LayoutGrid, SlidersHorizontal, Presentation } from "lucide-react";

const ICON_MAP: Record<string, React.ElementType> = {
  LayoutGrid, Hotel, Palmtree, Building2, PartyPopper, Trees, Waves, Building, Presentation,
};

const PRICE_RANGES_BY_CATEGORY: Record<string, { label: string; min: number; max: number }[]> = {
  hotel: [
    { label: "Under ₦150k/night", min: 0, max: 150_000 },
    { label: "₦150k – ₦300k", min: 150_000, max: 300_000 },
    { label: "₦300k – ₦600k", min: 300_000, max: 600_000 },
    { label: "₦600k+", min: 600_000, max: Infinity },
  ],
  "event-centre": [
    { label: "Under ₦300k/event", min: 0, max: 300_000 },
    { label: "₦300k – ₦600k", min: 300_000, max: 600_000 },
    { label: "₦600k – ₦1m", min: 600_000, max: 1_000_000 },
    { label: "₦1m+", min: 1_000_000, max: Infinity },
  ],
  "open-space": [
    { label: "Under ₦200k/event", min: 0, max: 200_000 },
    { label: "₦200k – ₦400k", min: 200_000, max: 400_000 },
    { label: "₦400k – ₦700k", min: 400_000, max: 700_000 },
    { label: "₦700k+", min: 700_000, max: Infinity },
  ],
};

const TITLES: Record<VenueCategory, { title: string; sub: string }> = {
  "hotel": { title: "Hotels & Stays", sub: "From boutique suites to grand resorts across Oyo State." },
  "event-centre": { title: "Event Centres", sub: "Halls, ballrooms and conference spaces for every occasion." },
  "open-space": { title: "Open Spaces", sub: "Outdoor lawns, rooftops and gardens for unforgettable events." },
};

interface Props { category: VenueCategory }

export function CategoryListing({ category }: Props) {
  const search = useSearch({ strict: false }) as any;
  const [location, setLocation] = useState<string>(search.location || "");
  const [price, setPrice] = useState<string>(search.price || "");
  const [facility, setFacility] = useState<string>(search.facility || "");
  const [activeTab, setActiveTab] = useState<string>("all");
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  useEffect(() => {
    if (search.location !== undefined) setLocation(search.location || "");
    if (search.price !== undefined) setPrice(search.price || "");
    if (search.facility !== undefined) setFacility(search.facility || "");
  }, [search.location, search.price, search.facility]);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const categoryPrices = PRICE_RANGES_BY_CATEGORY[category] || PRICE_RANGES;
      const range = categoryPrices.find((r) => r.label === price);
      const activeCat = BROWSABLE_CATEGORIES.find(c => c.id === activeTab);
      const facilityFilter = activeCat?.facility || facility;
      const results = await VenueService.getVenues({
        category: activeCat?.category || category,
        location,
        facility: facilityFilter,
        priceRange: range ? { min: range.min, max: range.max } : undefined,
      });
      setVenues(results);
      setLoading(false);
    };
    fetch();
  }, [category, location, facility, price, activeTab]);

  const [sort, setSort] = useState<"rating" | "price-asc" | "price-desc">("rating");
  const [page, setPage] = useState(1);
  const PER = 12;

  const sorted = useMemo(() => {
    const list = [...venues];
    if (sort === "rating") list.sort(compareByGuestRating);
    if (sort === "price-asc") list.sort((a, b) => getStartingPrice(a) - getStartingPrice(b));
    if (sort === "price-desc") list.sort((a, b) => getStartingPrice(b) - getStartingPrice(a));
    return list;
  }, [venues, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PER));
  const pageItems = sorted.slice((page - 1) * PER, page * PER);
  const meta = TITLES[category];

  const relevantCategories = BROWSABLE_CATEGORIES.filter(
    c => c.id === "all" || c.category === category || !c.category
  );

  return (
    <>
      <section className="bg-gradient-warm">
        <div className="mx-auto max-w-7xl px-4 pb-8 pt-14 sm:px-6 lg:px-8">
          <h1 className="font-display text-4xl font-bold sm:text-5xl">{meta.title}</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">{meta.sub}</p>
          <div className="mt-8"><SearchBar /></div>
        </div>
      </section>

      {/* Horizontal scrollable category tabs */}
      <section className="border-b border-border bg-background/95">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto py-3 scrollbar-hide">
            {relevantCategories.map((cat) => {
              const Icon = ICON_MAP[cat.icon] || LayoutGrid;
              const isActive = activeTab === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => { setActiveTab(cat.id); setPage(1); }}
                  className={`flex shrink-0 flex-col items-center gap-1 rounded-xl px-4 py-2 text-xs font-medium transition-all ${
                    isActive
                      ? "border-b-2 border-primary bg-primary/5 text-primary"
                      : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                  }`}
                >
                  <Icon className="size-5" />
                  <span>{cat.label}</span>
                </button>
              );
            })}

            <div className="ml-auto shrink-0 pl-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-medium hover:bg-accent/50 transition-colors"
              >
                <SlidersHorizontal className="size-4" />
                Filters
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Collapsible filter bar */}
        {showFilters && (
          <div className="mb-6 grid grid-cols-1 gap-3 rounded-2xl border border-border/60 bg-card p-4 shadow-sm animate-in slide-in-from-top-2 duration-200 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
            <select
              value={location}
              onChange={(e) => { setLocation(e.target.value); setPage(1); }}
              className="w-full rounded-full border border-border bg-background px-4 py-2 text-sm font-medium sm:w-auto"
              aria-label="Filter by location"
            >
              <option value="">All locations</option>
              {LOCATIONS.map((l) => <option key={l}>{l}</option>)}
            </select>
            <select
              value={facility}
              onChange={(e) => { setFacility(e.target.value); setPage(1); }}
              className="w-full rounded-full border border-border bg-background px-4 py-2 text-sm font-medium sm:w-auto"
              aria-label="Filter by facility type"
            >
              <option value="">All facilities</option>
              {FACILITIES.map((f) => <option key={f}>{f}</option>)}
            </select>
            <select
              value={price}
              onChange={(e) => { setPrice(e.target.value); setPage(1); }}
              className="w-full rounded-full border border-border bg-background px-4 py-2 text-sm font-medium sm:w-auto"
              aria-label="Filter by price range"
            >
              <option value="">Any price</option>
              {(PRICE_RANGES_BY_CATEGORY[category] || PRICE_RANGES).map((p) => <option key={p.label}>{p.label}</option>)}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
              className="w-full rounded-full border border-border bg-background px-4 py-2 text-sm font-medium sm:w-auto"
              aria-label="Sort venues"
            >
              <option value="rating">Top rated</option>
              <option value="price-asc">Price: low → high</option>
              <option value="price-desc">Price: high → low</option>
            </select>
            <div className="text-sm text-muted-foreground sm:ml-auto">
              {loading ? "..." : sorted.length} result{sorted.length !== 1 ? "s" : ""}
            </div>
          </div>
        )}

        {loading ? (
          <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[4/3] w-full rounded-xl" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-1/3" />
              </div>
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <div className="mx-auto max-w-md py-20 text-center">
            <div className="mx-auto mb-4 grid size-16 place-items-center rounded-full bg-muted">
              <MapPin className="size-8 text-muted-foreground" />
            </div>
            <h3 className="font-display text-xl font-semibold">No venues match your filters.</h3>
            <p className="mt-2 text-sm text-muted-foreground">Try adjusting location or price range.</p>
            <Button
              variant="outline"
              className="mt-6 rounded-full"
              onClick={() => {
                setLocation("");
                setPrice("");
                setFacility("");
                setActiveTab("all");
              }}
            >
              Clear all filters
            </Button>
          </div>
        ) : (
          <div className="grid gap-x-6 gap-y-10 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
            {pageItems.map((v) => (
              <VenueCard key={v.id} venue={v} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <Button key={i} variant={page === i + 1 ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setPage(i + 1)}>
                {i + 1}
              </Button>
            ))}
            <Button variant="outline" size="sm" className="rounded-full" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        )}
      </section>
    </>
  );
}

